import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MENU_ITEMS } from '../config/menu-meta';
import { MenuItem } from '../models/menu.model';
import { AuthService } from '../../../modules/shared/services/auth.service';
import { EmployeeModulesEnum } from '../../../modules/app/employees/enums/employee-modules.enum';
import { RestaurantService } from '../../../modules/shared/services/restaurant.service';

/** A single navigable destination matched by the topbar search. */
export interface MenuSearchResult {
  /** Translated leaf label, e.g. "Zniżki". */
  label: string;
  /** Router link to navigate to. */
  link: string;
  /** Translated ancestor labels (e.g. ["POS"]); shown as a muted breadcrumb. */
  breadcrumb: string[];
  /** Section header this result belongs to (translated title divider label). */
  section: string;
  /** Feather icon name inherited from the top-level menu item, if any. */
  icon?: string;
  /** Material Design icon class inherited from the top-level menu item, if any. */
  mdiIcon?: string;
}

/** A group of results sharing the same top-level section header. */
export interface MenuSearchGroup {
  section: string;
  results: MenuSearchResult[];
}

/**
 * Builds a searchable, permission-aware index of the manager-panel navigation
 * (the same MENU_ITEMS the left sidebar renders) and matches it against a free
 * text query. Used by the topbar global search ("command palette").
 *
 * Matching is diacritics-insensitive (so "zniz" matches "Zniżki") and runs over
 * both the leaf label and its section, so typing a section name lists all of its
 * destinations. Items the current user cannot access (by EmployeeModule) are
 * excluded, mirroring the sidebar's canSee() logic.
 */
@Injectable({ providedIn: 'root' })
export class MenuSearchService {
  /** Max results returned to keep the dropdown compact. */
  private readonly maxResults = 10;

  /** Combining diacritical marks (U+0300–U+036F) stripped after NFD decomposition. */
  private readonly combiningMarks = new RegExp('[\\u0300-\\u036f]', 'g');

  constructor(private translate: TranslateService, private authService: AuthService, private restaurantService?: RestaurantService) {}

  /**
   * Returns matching destinations grouped by section, in menu order.
   * Empty/blank query returns an empty list.
   */
  search(query: string): MenuSearchGroup[] {
    const needle = this.normalize(query);
    if (!needle) {
      return [];
    }

    const all = this.buildIndex();
    const seen: { [link: string]: boolean } = {};
    const matched: MenuSearchResult[] = [];

    for (const entry of all) {
      if (matched.length >= this.maxResults) {
        break;
      }
      if (seen[entry.link]) {
        continue;
      }
      const haystack = this.normalize(entry.label + ' ' + entry.section + ' ' + entry.breadcrumb.join(' '));
      if (haystack.indexOf(needle) === -1) {
        continue;
      }
      seen[entry.link] = true;
      matched.push(entry);
    }

    return this.groupBySection(matched);
  }

  /** Flatten MENU_ITEMS into accessible, navigable leaf entries (menu order). */
  private buildIndex(): MenuSearchResult[] {
    const out: MenuSearchResult[] = [];
    let currentSection = '';

    for (const item of MENU_ITEMS) {
      if (item.isTitle) {
        currentSection = this.t(item.label);
        continue;
      }
      this.walk(item, true, [], currentSection, item.icon, item.mdiIcon, out);
    }

    return out;
  }

  /** Recursively collect a node and its children, carrying access + breadcrumb. */
  private walk(
    node: MenuItem,
    parentAccessible: boolean,
    breadcrumb: string[],
    section: string,
    rootIcon: string | undefined,
    rootMdiIcon: string | undefined,
    out: MenuSearchResult[],
  ): void {
    const accessible = parentAccessible && this.canSee(node.module) && this.isAvailableForRestaurants(node);
    const label = this.t(node.label);

    if (accessible && node.link) {
      out.push({ label, link: node.link, breadcrumb, section, icon: rootIcon, mdiIcon: rootMdiIcon });
    }

    if (node.children && node.children.length) {
      const childBreadcrumb = breadcrumb.concat(label);
      for (const child of node.children) {
        this.walk(child, accessible, childBreadcrumb, section, rootIcon, rootMdiIcon, out);
      }
    }
  }

  /** Group matched results by section, preserving first-seen section order. */
  private groupBySection(results: MenuSearchResult[]): MenuSearchGroup[] {
    const groups: MenuSearchGroup[] = [];
    const bySection: { [section: string]: MenuSearchGroup } = {};

    for (const result of results) {
      const key = result.section || '';
      if (!bySection[key]) {
        bySection[key] = { section: key, results: [] };
        groups.push(bySection[key]);
      }
      bySection[key].results.push(result);
    }

    return groups;
  }

  /** Mirrors the sidebar: no module means always visible. */
  private canSee(module?: EmployeeModulesEnum): boolean {
    return !module || this.authService.hasAccess(module, false);
  }

  private isAvailableForRestaurants(node: MenuItem): boolean {
    if (!node.restaurantIds) {
      return true;
    }

    const restaurants = (this.restaurantService && this.restaurantService.allRestaurants) || [];

    return restaurants.some(restaurant => node.restaurantIds.includes(Number(restaurant.id)));
  }

  /** Synchronous translation of a menu label key (translations are preloaded). */
  private t(key?: string): string {
    if (!key) {
      return '';
    }
    const value = this.translate.instant(key);
    // instant() echoes the key back when missing — fall back to the raw key.
    return value || key;
  }

  /**
   * Lowercase + strip diacritics so "Zniż"/"zniz" both match "Zniżki".
   * NFD decomposes most accents; Polish ł is handled explicitly.
   */
  private normalize(value: string): string {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(this.combiningMarks, '')
      .replace(/ł/g, 'l')
      .trim();
  }
}
